// The interface in four languages: O‘zbek (the default), English, Русский and
// Deutsch.
//
// Every string a person reads lives in a namespace file next to this one, and
// every namespace is written in Uzbek first: `defineMessages(uz, { en, ru, de })`
// makes the compiler refuse a namespace in which any other language is missing
// a key, so a half-translated screen cannot be built. What is data — a course
// title, a lesson's text — is never translated; only the interface around it.
//
// The language is chosen, in order, by: what this browser was last told
// (localStorage), the signed-in account's own setting, the tenant's language
// (the learner app reports it), and Uzbek. Choosing one here stores it, and for
// a signed-in person also saves it to their account, so it follows them to the
// other app and the next device.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { CSSProperties, JSX, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { api } from '../api'

// ------------------------------------------------------------------- types --

export type Lang = 'uz' | 'en' | 'ru' | 'de'

/** The languages on offer, each named in itself, with its flag. */
export const LANGS: ReadonlyArray<{ code: Lang; label: string; flag: string }> = [
  { code: 'uz', label: 'O‘zbek', flag: '🇺🇿' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
]

/** What Intl is asked for. en-GB rather than en-US: day before month, 24 hours. */
const LOCALES: Record<Lang, string> = { uz: 'uz-UZ', en: 'en-GB', ru: 'ru-RU', de: 'de-DE' }

/** Where this browser remembers the choice. Both apps read the same key. */
const STORAGE_KEY = 'mymentor.lang'

export type Vars = Record<string, string | number>

/**
 * One message: a string with `{name}` placeholders, or a function for grammar a
 * placeholder cannot express — a Russian plural, say. Whatever the function
 * returns has its placeholders filled in too.
 */
export type Msg = string | ((v: Vars, lang: Lang) => string)

declare const keysOf: unique symbol

/**
 * A namespace. Opaque on purpose: it is read through `useT` and `translate`,
 * which is what keeps a key that does not exist a compile error.
 */
export interface Messages<U extends Record<string, Msg>> {
  /** Carries the key set for the type checker; never present at runtime. */
  readonly [keysOf]?: U
  /** @internal */
  readonly table: { readonly [L in Lang]: Readonly<Record<string, Msg>> }
}

/**
 * Declares a namespace. Uzbek is the source; English, Russian and German must
 * each carry exactly its keys — a missing one fails `tsc`.
 */
export function defineMessages<U extends Record<string, Msg>>(
  uz: U,
  others: {
    en: Record<keyof NoInfer<U>, Msg>
    ru: Record<keyof NoInfer<U>, Msg>
    de: Record<keyof NoInfer<U>, Msg>
  },
): Messages<U> {
  return { table: { uz, en: others.en, ru: others.ru, de: others.de } }
}

// --------------------------------------------------------------- language --

/** Reads a stored or reported language tag ("ru", "uz-Latn", "de-DE") as one of ours. */
export function asLang(raw: string | null | undefined): Lang | null {
  if (!raw) return null
  const primary = raw.trim().toLowerCase().split(/[-_]/)[0]
  return LANGS.some((l) => l.code === primary) ? (primary as Lang) : null
}

function readStored(): Lang | null {
  try {
    return asLang(localStorage.getItem(STORAGE_KEY))
  } catch {
    // Storage can be switched off; the choice then lasts as long as the page.
    return null
  }
}

function store(lang: Lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    // As above: not remembering is better than not switching.
  }
}

// The language outside React. `translate`, `when()` and error messages are
// called from places that cannot use a hook — a toast fired from a callback, a
// date in a table helper — so the provider keeps this in step with its state.
let current: Lang = readStored() ?? 'uz'

/** The language right now, for code that cannot use `useI18n`. */
export function currentLang(): Lang {
  return current
}

/** The Intl locale for the language right now. */
export function currentLocale(): string {
  return LOCALES[current]
}

// ---------------------------------------------------------------- messages --

function fill(text: string, vars?: Vars): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = vars[name]
    return v === undefined ? whole : String(v)
  })
}

function render(m: Messages<Record<string, Msg>>, key: string, vars: Vars | undefined, lang: Lang): string | undefined {
  // Uzbek is complete by construction, so it is the fallback for a language a
  // namespace somehow lacks at runtime — never an empty space in the page.
  const msg = m.table[lang][key] ?? m.table.uz[key]
  if (msg === undefined) return undefined
  const text = typeof msg === 'function' ? msg(vars ?? {}, lang) : msg
  return fill(text, vars)
}

/** Translates outside React, in the current language. */
export function translate<U extends Record<string, Msg>>(m: Messages<U>, key: keyof U & string, vars?: Vars): string {
  return render(m as Messages<Record<string, Msg>>, key, vars, current) ?? key
}

/**
 * Translates a key that is only known at runtime — an error code from the
 * server, say — or answers undefined when the namespace does not have it.
 */
export function translateIfAny<U extends Record<string, Msg>>(m: Messages<U>, key: string, vars?: Vars): string | undefined {
  return render(m as Messages<Record<string, Msg>>, key, vars, current)
}

/** The translator for one namespace, in the language on screen. */
export function useT<U extends Record<string, Msg>>(m: Messages<U>): (key: keyof U & string, vars?: Vars) => string {
  const { lang } = useI18n()
  return useCallback(
    (key: keyof U & string, vars?: Vars) => render(m as Messages<Record<string, Msg>>, key, vars, lang) ?? key,
    [m, lang],
  )
}

/**
 * Picks the plural form a number needs. Russian has three (1 курс, 2 курса,
 * 5 курсов, and 21 курс again); English and German two; Uzbek does not change
 * the noun after a number at all, but one/other is kept for the odd sentence
 * that reads differently for exactly one. `{n}` in the chosen form is the
 * number itself, so the result can also be used on its own.
 */
export function plural(
  lang: Lang,
  n: number,
  forms: { one: string; few?: string; many?: string; other: string },
): string {
  return (forms[pluralCategory(lang, n)] ?? forms.other).replace(/\{n\}/g, String(n))
}

function pluralCategory(lang: Lang, n: number): 'one' | 'few' | 'many' | 'other' {
  const abs = Math.abs(n)
  if (lang === 'ru') {
    if (!Number.isInteger(abs)) return 'other'
    const mod10 = abs % 10
    const mod100 = abs % 100
    if (mod10 === 1 && mod100 !== 11) return 'one'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few'
    return 'many'
  }
  return abs === 1 ? 'one' : 'other'
}

// ---------------------------------------------------------------- formats --

const formats = new Map<string, Intl.DateTimeFormat>()

function dateFormat(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = locale + JSON.stringify(opts)
  let f = formats.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts)
    formats.set(key, f)
  }
  return f
}

/** Formats a timestamp from the API, or a dash where there is none. */
export function formatDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions, locale = currentLocale()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  if (locale.startsWith('uz')) return formatUz(d, opts)
  return dateFormat(locale, opts).format(d)
}

const UZ_MONTHS_SHORT = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avg', 'sen', 'okt', 'noy', 'dek']
const UZ_MONTHS_LONG = [
  'yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr',
]

/**
 * Uzbek dates are written here rather than asked of Intl. Browsers disagree
 * about Uzbek — Chromium builds without its locale data print "2026 M09 29" —
 * and it is the language most readers of this interface use, so it is the one
 * that cannot be left to chance. The shapes follow CLDR's: 29-sen, 2026 for a
 * date in words, and the dotted 29.09.26 that Uzbek paperwork uses for the
 * numeric one.
 */
function formatUz(d: Date, opts: Intl.DateTimeFormatOptions): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const o: Intl.DateTimeFormatOptions = { ...opts }
  if (o.dateStyle) Object.assign(o, { day: 'numeric', month: o.dateStyle === 'short' ? '2-digit' : 'short', year: 'numeric' })
  if (o.timeStyle) Object.assign(o, { hour: '2-digit', minute: '2-digit' })

  const out: string[] = []
  const year = o.year === '2-digit' ? pad(d.getFullYear() % 100) : String(d.getFullYear())
  if (o.month === 'short' || o.month === 'long' || o.month === 'narrow') {
    const month = (o.month === 'long' ? UZ_MONTHS_LONG : UZ_MONTHS_SHORT)[d.getMonth()]
    const day = o.day === '2-digit' ? pad(d.getDate()) : String(d.getDate())
    out.push(`${o.day ? `${day}-${month}` : month}${o.year ? `, ${year}` : ''}`)
  } else if (o.day || o.month || o.year) {
    const bits: string[] = []
    if (o.day) bits.push(pad(d.getDate()))
    if (o.month) bits.push(pad(d.getMonth() + 1))
    if (o.year) bits.push(year)
    out.push(bits.join('.'))
  }
  if (o.hour) out.push(`${pad(d.getHours())}:${pad(d.getMinutes())}`)
  return out.join(', ')
}

/**
 * Numbers in Uzbek group digits with a space and use a decimal comma, exactly
 * as Russian does; ru-RU is used for them because every browser has it.
 */
function numberLocale(locale: string): string {
  return locale.startsWith('uz') ? 'ru-RU' : locale
}

const DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
const TIME: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }

export type Format = {
  date(iso?: string | null): string
  dateTime(iso?: string | null): string
  time(iso?: string | null): string
  number(n: number): string
}

function formatterFor(lang: Lang): Format {
  const locale = LOCALES[lang]
  const numbers = new Intl.NumberFormat(numberLocale(locale))
  return {
    date: (iso) => formatDate(iso, DATE, locale),
    dateTime: (iso) => formatDate(iso, { ...DATE, ...TIME }, locale),
    time: (iso) => formatDate(iso, TIME, locale),
    number: (n) => (Number.isFinite(n) ? numbers.format(n) : '—'),
  }
}

/** Dates and numbers written the way the language on screen writes them. */
export function useFormat(): Format {
  const { lang } = useI18n()
  return useMemo(() => formatterFor(lang), [lang])
}

// ---------------------------------------------------------------- provider --

type Ctx = { lang: Lang; setLang(l: Lang): void; locale: string }

const I18nContext = createContext<Ctx | null>(null)

/** What the account and the tenant say, reported from inside their providers. */
export type LanguageHints = {
  /** The signed-in person's saved language; null when nobody is signed in. */
  account?: string | null
  signedIn?: boolean
  /** The tenant's language (learner app). */
  platform?: string | null
}

type Hints = { account: Lang | null; signedIn: boolean; platform: Lang | null }

const HintsContext = createContext<(h: LanguageHints) => void>(() => undefined)

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [chosen, setChosen] = useState<Lang | null>(readStored)
  const [hints, setHints] = useState<Hints>({ account: null, signedIn: false, platform: null })

  const lang: Lang = chosen ?? hints.account ?? hints.platform ?? 'uz'
  // Set during render rather than in an effect, so that `translate` called by
  // a child rendering in this same pass already answers in the new language.
  current = lang

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  // Another tab switching language switches this one too.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setChosen(asLang(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const signedIn = useRef(false)
  signedIn.current = hints.signedIn

  const setLang = useCallback((l: Lang) => {
    store(l)
    setChosen(l)
    if (signedIn.current) {
      // Saved to the account so it follows the person to their next device.
      // A failure is not shown: the choice already holds here, and a server
      // that cannot store it yet is no reason to say so. A 401 is the
      // exception, and is not quiet: it means the session itself has ended —
      // a sign-in on another device, say, when switching the language is the
      // first thing done on a PC that kept the focus meanwhile — so like any
      // other call's it goes to the signed-out handling, which takes this tab
      // to the sign-in page and says why.
      api('/auth/me/locale', { method: 'PATCH', body: { locale: l } }).catch(() => undefined)
    }
  }, [])

  const report = useCallback((h: LanguageHints) => {
    setHints((prev) => {
      const next: Hints = {
        account: h.account !== undefined ? asLang(h.account) : prev.account,
        signedIn: h.signedIn ?? prev.signedIn,
        platform: h.platform !== undefined ? asLang(h.platform) : prev.platform,
      }
      return next.account === prev.account && next.signedIn === prev.signedIn && next.platform === prev.platform
        ? prev
        : next
    })
  }, [])

  const value = useMemo<Ctx>(() => ({ lang, setLang, locale: LOCALES[lang] }), [lang, setLang])

  return (
    <HintsContext.Provider value={report}>
      <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
    </HintsContext.Provider>
  )
}

/**
 * Reports what the account or the tenant says about language. Rendered by the
 * small bridges in main.tsx, inside the providers that know those things.
 */
export function useLanguageHints(h: LanguageHints): void {
  const report = useContext(HintsContext)
  const { account, signedIn, platform } = h
  useEffect(() => {
    report({ account, signedIn, platform })
  }, [report, account, signedIn, platform])
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext)
  // Outside a provider (a test harness, a portal rendered too early) there is
  // still a language: the one this browser remembers.
  return ctx ?? { lang: current, setLang: () => undefined, locale: LOCALES[current] }
}

// ---------------------------------------------------------------- switcher --

const own = defineMessages(
  {
    language: 'Til',
    change: 'Tilni o‘zgartirish, hozirgi til: {current}',
  },
  {
    en: { language: 'Language', change: 'Change language, current: {current}' },
    ru: { language: 'Язык', change: 'Сменить язык, сейчас: {current}' },
    de: { language: 'Sprache', change: 'Sprache wechseln, aktuell: {current}' },
  },
)

/**
 * The language picker: a flag and the language's own name, opening a list of
 * all four. `compact` shows only the flag, for a crowded bar.
 *
 * The flags are drawn rather than typed. An emoji flag is two letters on
 * Windows, which is where most of the people using this sit.
 *
 * The list is drawn over the page, beside the button, rather than inside
 * whatever holds the button: the learner's phone header scrolls sideways and
 * would otherwise cut it off.
 */
export function LanguageSwitcher({ compact }: { compact?: boolean }): JSX.Element {
  const { lang, setLang } = useI18n()
  const t = useT(own)
  const id = useId()
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<CSSProperties | null>(null)
  const [active, setActive] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const selected = LANGS.find((l) => l.code === lang) ?? LANGS[0]!

  const show = () => {
    setActive(Math.max(0, LANGS.findIndex((l) => l.code === lang)))
    setOpen(true)
  }

  // Below the button, or above it where there is more room (a sidebar foot),
  // right edges aligned, and always inside the window.
  const reposition = useCallback(() => {
    const b = button.current?.getBoundingClientRect()
    const l = list.current
    if (!b || !l) return
    const gap = 6
    const margin = 8
    const h = l.offsetHeight
    const w = l.offsetWidth
    const below = window.innerHeight - b.bottom
    const up = below < h + gap + margin && b.top > below
    const top = up ? Math.max(margin, b.top - gap - h) : Math.min(b.bottom + gap, window.innerHeight - h - margin)
    const left = Math.min(Math.max(margin, b.right - w), window.innerWidth - w - margin)
    setPlace({ position: 'fixed', top, left })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  const hide = (refocus: boolean) => {
    setOpen(false)
    if (refocus) button.current?.focus()
  }

  const choose = (code: Lang) => {
    setLang(code)
    hide(true)
  }

  // Focus goes in once the list is placed; hidden while measured, it could not take it.
  const placed = open && place !== null
  useEffect(() => {
    if (!placed) return
    list.current?.focus({ preventScroll: true })
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (root.current?.contains(target) || list.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [placed])

  const onButtonKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      show()
    }
  }

  const onListKey = (e: ReactKeyboardEvent) => {
    const last = LANGS.length - 1
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => (i >= last ? 0 : i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => (i <= 0 ? last : i - 1))
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(last)
        break
      case 'Enter':
      case ' ': {
        e.preventDefault()
        const pick = LANGS[active]
        if (pick) choose(pick.code)
        break
      }
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        hide(true)
        break
      case 'Tab':
        setOpen(false)
        break
    }
  }

  return (
    <div className={compact ? 'lang lang--compact' : 'lang'} ref={root}>
      <button
        ref={button}
        type="button"
        className="lang__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-label={t('change', { current: selected.label })}
        title={compact ? selected.label : undefined}
        onClick={() => (open ? hide(false) : show())}
        onKeyDown={onButtonKey}
      >
        <Flag code={selected.code} />
        {!compact && (
          <span className="lang__label" lang={selected.code}>
            {selected.label}
          </span>
        )}
        <svg className="lang__caret" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
          <path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open &&
        createPortal(
          <ul
            ref={list}
            id={`${id}-list`}
            role="listbox"
            tabIndex={-1}
            className="lang__menu"
            style={place ?? { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }}
            aria-label={t('language')}
            aria-activedescendant={`${id}-${LANGS[active]?.code ?? lang}`}
            onKeyDown={onListKey}
          >
            {LANGS.map((l, i) => (
              <li
                key={l.code}
                id={`${id}-${l.code}`}
                role="option"
                aria-selected={l.code === lang}
                lang={l.code}
                className={i === active ? 'lang__option is-active' : 'lang__option'}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(l.code)}
              >
                <Flag code={l.code} />
                <span className="grow">{l.label}</span>
                {l.code === lang && (
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                    <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  )
}

/** A national flag, 3:2, drawn small enough to sit beside a line of text. */
export function Flag({ code }: { code: Lang }): JSX.Element {
  const clip = useId()
  return (
    <span className="flag" aria-hidden="true">
      <svg viewBox="0 0 30 20" width="21" height="14" preserveAspectRatio="xMidYMid slice" focusable="false">
        {code === 'uz' && (
          <>
            <rect width="30" height="20" fill="#FFFFFF" />
            <rect width="30" height="6.4" fill="#0099B5" />
            <rect y="13.6" width="30" height="6.4" fill="#1EB53A" />
            <rect y="6.4" width="30" height="0.7" fill="#CE1126" />
            <rect y="12.9" width="30" height="0.7" fill="#CE1126" />
            <circle cx="5" cy="3.2" r="2.3" fill="#FFFFFF" />
            <circle cx="5.9" cy="3.2" r="2" fill="#0099B5" />
            <g fill="#FFFFFF">
              <circle cx="9.4" cy="1.6" r="0.42" />
              <circle cx="10.8" cy="1.6" r="0.42" />
              <circle cx="12.2" cy="1.6" r="0.42" />
              <circle cx="8" cy="3.2" r="0.42" />
              <circle cx="9.4" cy="3.2" r="0.42" />
              <circle cx="10.8" cy="3.2" r="0.42" />
              <circle cx="12.2" cy="3.2" r="0.42" />
              <circle cx="9.4" cy="4.8" r="0.42" />
              <circle cx="10.8" cy="4.8" r="0.42" />
              <circle cx="12.2" cy="4.8" r="0.42" />
            </g>
          </>
        )}
        {code === 'en' && (
          <>
            <clipPath id={clip}>
              <path d="M15 10h15v10zv10H0zH0V0zV0h15z" />
            </clipPath>
            <rect width="30" height="20" fill="#012169" />
            <path d="M0 0l30 20M30 0L0 20" stroke="#FFFFFF" strokeWidth="4" />
            <path d="M0 0l30 20M30 0L0 20" stroke="#C8102E" strokeWidth="2.2" clipPath={`url(#${clip})`} />
            <path d="M15 0v20M0 10h30" stroke="#FFFFFF" strokeWidth="6" />
            <path d="M15 0v20M0 10h30" stroke="#C8102E" strokeWidth="3.4" />
          </>
        )}
        {code === 'ru' && (
          <>
            <rect width="30" height="20" fill="#FFFFFF" />
            <rect y="6.67" width="30" height="6.67" fill="#0039A6" />
            <rect y="13.33" width="30" height="6.67" fill="#D52B1E" />
          </>
        )}
        {code === 'de' && (
          <>
            <rect width="30" height="6.67" fill="#000000" />
            <rect y="6.67" width="30" height="6.67" fill="#DD0000" />
            <rect y="13.33" width="30" height="6.67" fill="#FFCE00" />
          </>
        )}
      </svg>
    </span>
  )
}
